import * as React from 'react';
import { createSHA256 } from 'hash-wasm';
import { v4 as uuidv4 } from 'uuid';
import { gzip } from "pako";
import { useAuth } from "react-oidc-context";
import { useDropzone } from 'react-dropzone';
import { GlobalAuthorization } from "../misc/components/Authorization";
import { useSecurityContext } from "../Core/SecurityContext";
import { PermissionEnum } from "../misc/api/ApiPermissionEnum";
import { useState, useRef, useCallback, useMemo } from 'react';
import { FormControl, ProgressBar, HelpBlock} from 'react-bootstrap';
import Alert, { AlertCategoryEnum } from '../misc/components/Alert';
import { useCoughMarshalApiClient } from '../misc/api/CoughMarshalApiClient';
import { useWebPortalApiClient, useWebPortalLegacyClient } from '../misc/api/WebPortalTokenClient';
import { authenticatedFetchRetryWithAbort } from '../misc/api/FetchRetry';
import { CoughSession } from 'webportalclient';
import { useCurrentStudyConfigurationContext } from '../Core/CurrentStudyConfigurationContext';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCircleNotch } from '@fortawesome/free-solid-svg-icons';
import { useIdleTimerContext } from '../Core/IdleTimerContext';
import { CoughProcessingAlgorithmEnum, CoughSessionStatusEnum } from '../misc/api/apiTypes';
import { GetRecordingTags } from "./UploadCoughSessionsAsyncAction";

declare const coughMarshalRootUrl: string;

// Create alert message types.

export type AlertMessage = {
    type: AlertCategoryEnum;
    message: AlertType;
}

export enum AlertType {
    FILE_NAME = 'Please rename all files as required format.',
    FILE_PATTERN = 'The following files are incomplete:',
    CONFIGURE_ERROR = 'Upload failed, please check if you have correct site/subject/visit/session configuration.',
    ERROR = 'Failed to upload files.',
    SUCCESS = 'Upload succeeded!',
    FILE_LENGTH = 'Only 2 files are allowed for each session, please upload even number of files.',
    WARNING = 'Files have already been uploaded.'
}

type FileGroup = {
    identifier: string;
    files: File[];
};

export const UploadFileModal = () => {

    const [selectedFiles, setSelectedFiles] = useState<File[] | undefined>();
    const [isUploading, setIsUploading] = useState<boolean>(false);
    const [uploadedFileNumber, setUploadedFileNumber] = useState<number | undefined>(0);
    const [isComplete, setIsComplete] = useState<boolean>(false);
    const [alert, setAlert] = useState<AlertMessage | null>(null);

    const sessionRef = useRef<CoughSession>();
    const totalFileRef = useRef<number | undefined>(0);
    const idRef = useRef<string>('');
    const abortController = useRef<AbortController | null>(null);
    const unAcceptedFile = useRef<FileGroup[]>([]);

    const { study } = useCurrentStudyConfigurationContext();
    const cmClient = useCoughMarshalApiClient();
    const legacyClient = useWebPortalLegacyClient();
    const { client: wpClient } = useWebPortalApiClient();
    const auth = useAuth();
    const { currentUserPermissions } = useSecurityContext();
    const { timer } = useIdleTimerContext();

    const retryCount = 3;
    const retryInterval = 50000;
    const chunkSize = 10485760;
    let allUploadsSuccessful = null;
    let fileCorrectness = null;

    const showAlert = (alertMessage: AlertMessage) => {
        setAlert(alertMessage);
    };

    const showUnAcceptedFile = () => {
        const fileNames: string[] = [];

        unAcceptedFile.current.forEach(fileGroup => {
            fileGroup.files.forEach(file => {
                fileNames.push(file.name);
            });
        })
        return (<>
            <HelpBlock>The following files have been skipped as they have already been uploaded:</HelpBlock>
            <ul>
                {fileNames.map((fileName, index) => (
                    <li key={index}>{fileName}</li>
                ))}
            </ul> </>
        );
    }

    // Create a drop zone and specify acceptable file format.
    const onDrop = useCallback((selectedFiles) => {
        setSelectedFiles(selectedFiles);
        setAlert(null);  //Clear the alert when selectedFiles changes.
        unAcceptedFile.current = [];
    }, []);

    const { getRootProps, getInputProps, isDragActive, acceptedFiles, isFocused, isDragAccept, isDragReject } = useDropzone({
        onDrop,
        accept: {
            'audio/wav': []
        }
    });

    const files = acceptedFiles.map(file => <li key={file.name}>{file.name}</li>);

    // Styling the drop zone according to whether the file format is acceptable or not.
    const baseStyle = {
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        padding: '20px',
        borderWidth: 2,
        borderRadius: 2,
        borderColor: '#eeeeee',
        borderStyle: 'dashed',
        backgroundColor: '#fafafa',
        color: '#bdbdbd',
        outline: 'none',
        transition: 'border .24s ease-in-out'
    };

    const focusedStyle = {
        borderColor: '#006BA6'
    };

    const acceptStyle = {
        borderColor: '#006BA6'
    };

    const rejectStyle = {
        borderColor: '#ed443f',
        fontColor: '#ed443f'
    };


    // Re-evaluate styles only when some variables change.
    const dropzonestyle = useMemo(() => ({
        ...baseStyle,
        ...(isFocused ? focusedStyle : {}),
        ...(isDragAccept ? acceptStyle : {}),
        ...(isDragReject ? rejectStyle : {})
    }), [
        isFocused,
        isDragAccept,
        isDragReject
    ]);

    const computeFileHash = async (recordingFile: File) => {
        const hash = await createSHA256();
        hash.init();
        const reader = recordingFile.stream().getReader();

        while (true) {
            const { done, value } = await reader.read();
            if (done) break;
            hash.update(value);
        }

        return hash.digest('binary');
    }

    const uploadRecordings = async (recordingFiles: File[]) => {

        const controller = new AbortController();
        abortController.current = controller;

        // Determin subject/visit/session from file name.
        // <site>__<subject>__<visit>__<session>__WAV<file>.wav
        const metaRegex = /^(?<site>.+)__(?<subject>.+)__(?<visit>.+)__(?<session>.+)__WAV(?<file>(1|2)).wav$/;

        // Check the format, number, and status of files before uploading, and show the corresponding alert if needed.
        const checkingFiles = (): boolean => {

            if (recordingFiles.length % 2 != 0) {
                showAlert({ type: AlertCategoryEnum.Warning, message: AlertType.FILE_LENGTH });
                return false;
            }

            for (const recordingFile of recordingFiles) {

                if (!metaRegex.test(recordingFile.name)) {
                    showAlert({ type: AlertCategoryEnum.Warning, message: AlertType.FILE_NAME });
                    return false;

                } else if (sessionRef.current != undefined &&
                    (sessionRef.current.status != CoughSessionStatusEnum.AwaitingData && sessionRef.current.status != CoughSessionStatusEnum.ReceivingData)) {
                    showAlert({ type: AlertCategoryEnum.Danger, message: AlertType.WARNING });
                    return false;
                }
            }
            return true;
        }

        const passCheckingFiles = checkingFiles();

        if (!passCheckingFiles) return;

        //Keep connection on during upload process.
        timer.pause();

        //Sorting files into groups according to their names, each group has 2 files
        //with the same site, subject, visit and session prefix.
        const sortFiles = (files: File[], metaRegex: RegExp): FileGroup[] => {

            const fileMap: { [key: string]: File[] } = {};

            files.forEach((file) => {
                const match = metaRegex.exec(file.name);
                if (match) {
                    const { site, subject, visit, session } = match.groups!;
                    const identifier = `${site}__${subject}__${visit}__${session}`;
                    if (!fileMap[identifier]) {
                        fileMap[identifier] = [];
                    }
                    fileMap[identifier].push(file);
                }
            });
            const sortedFiles: FileGroup[] = [];

            for (const identifier in fileMap) {
                if (fileMap[identifier].length === 2) {
                    sortedFiles.push({ identifier, files: fileMap[identifier] });
                } else {
                    unAcceptedFile.current.push({ identifier, files: fileMap[identifier] });
                }
            }

            return sortedFiles;
        };

        const sortedFileGroups = sortFiles(recordingFiles, metaRegex);

        //Check if all the files are correct and ready to be uploaded. 
        if (sortedFileGroups.length != recordingFiles.length / 2) {
            showAlert({ type: AlertCategoryEnum.Warning, message: AlertType.FILE_PATTERN });
        } else {
            fileCorrectness = true;
        }

        //If passed the file-check process, iterate through recordings and upload.
        for (const group of sortedFileGroups) {

            if (!fileCorrectness) break;

            const meta = metaRegex.exec(group.files[0].name).groups;
            setIsUploading(true);

            try {
                // If needed create subject/visit/session on portal, skip if session already uploaded.
                const sites = await wpClient.getSites(undefined, `studyId eq ${study.id} and number eq '${meta.site}'`, undefined, undefined, 1);
                const site = sites.value.length > 0
                    ? sites.value[0]
                    : (await wpClient.postSites({ studyId: study.id, number: meta.site, country: `${study.countriesIds[0]}`, timeZone: "GMT Standard Time", name: `site ${meta.site}`, phone: "111" }));

                const subjects = await wpClient.getSubjects(undefined, `number eq '${meta.subject}' and site/study/id eq ${study.id}`, undefined, undefined, 1);
                const subject = subjects.value.length > 0
                    ? subjects.value[0]
                    : (await wpClient.postSubjects({ siteId: site.id, number: meta.subject }));

                const visits = await wpClient.getVisits(undefined, `subjectId eq ${subject.id} and name eq '${meta.visit}'`, undefined, undefined, 1);
                const visit = visits.value.length > 0
                    ? visits.value[0]
                    : (await wpClient.postVisit({ subjectId: subject.id, name: meta.visit }));

                const sessions = await wpClient.getCoughSessions(undefined, `visitId eq ${visit.id} and name eq '${meta.session}'`, undefined, undefined, 1);

                if (sessions.value.length > 0) {
                    if (sessions.value[0].status == CoughSessionStatusEnum.AwaitingData || sessions.value[0].status == CoughSessionStatusEnum.ReceivingData) {
                        idRef.current = sessions.value[0].id;
                    } else {
                        unAcceptedFile.current.push({ identifier: group.identifier, files: group.files });
                        showAlert({ type: AlertCategoryEnum.Danger, message: AlertType.WARNING });
                        continue;
                    }

                } else {
                    idRef.current = uuidv4();
                    const postSessionResponse = await wpClient.postCoughSession({
                        visitId: visit.id,
                        name: meta.session,
                        id: idRef.current,
                        status: "CSS_AWAITING_DATA",
                        deviceType: "DT_VITALOJAK_I"
                    });
                    if (!postSessionResponse) {
                        showAlert({ type: AlertCategoryEnum.Danger, message: AlertType.CONFIGURE_ERROR });
                        stopUpload();
                    }
                }
                const recordingTags = await GetRecordingTags(legacyClient, study.protocol, study.id);

                let existingRecording
                try {
                    existingRecording = await cmClient.getRecording(idRef.current);
                }
                catch (err) {
                    if ("status" in (err as any)) {
                        if ((err as any)?.status === 404) {
                            existingRecording = null;
                        } else {
                            stopUpload();
                            showAlert({ type: AlertCategoryEnum.Danger, message: err.message });
                        }
                    } else {
                        stopUpload();
                        showAlert({ type: AlertCategoryEnum.Danger, message: err.message });
                    }
                }
                const processingAlgorithmsSetting = await legacyClient.getSetting('cough.algorithmConfiguration.processingAlgorithms', study.id);
                const processingAlgorithms = processingAlgorithmsSetting.map(pa => CoughProcessingAlgorithmEnum[pa as keyof typeof CoughProcessingAlgorithmEnum])

                if (existingRecording === null || existingRecording === undefined) {
                    const requestBody = {
                        id: idRef.current,
                        tags: [study.protocol],
                        processingAlgorithms: processingAlgorithms
                    }
                    const response = await cmClient.putRecording(requestBody);
                    idRef.current = response.id;
                }

            } catch (err) {
                showAlert({ type: AlertCategoryEnum.Danger, message: err.message });
                stopUpload();
                return;
            }

            for (let i = 0; i < group.files.length; i++) {

                if (!fileCorrectness) {
                    stopUpload();
                    break;
                }

                const recordingFile = group.files[i];
                const fileSize = recordingFile.size;

                const fileMatch = metaRegex.exec(recordingFile.name);
                const fileNumber = fileMatch.groups!.file;

                // Pass the user as a parameter to upload files, which included the authorization information.
                const uploadFileToCoughMarshal = async (user): Promise<boolean> => {

                    let startIndex: number = 0;

                    try {
                        const headResult = await authenticatedFetchRetryWithAbort(user, `${coughMarshalRootUrl}/api/v1/uploads/${idRef.current}`, {
                            method: 'HEAD',
                            headers: {
                                'Content-Disposition': `attachment; filename="24hrWAV${fileNumber}.wav"`
                            }
                        }, retryCount, retryInterval, abortController.current)

                        if (headResult.status != 200) {
                            showAlert({ type: AlertCategoryEnum.Danger, message: AlertType.ERROR });
                            stopUpload();
                            return false;
                        }
                        const uploadedBytes = parseInt(headResult.headers.get('Content-Length'), 10);

                        // Check if the file is fully uploaded
                        if (uploadedBytes === fileSize) {
                            return true; // Skip re-uploading this file
                        } else {
                            // Set startIndex to the byte where the upload left off
                            startIndex = uploadedBytes;
                        }
                    } catch (err) {
                        throw new Error(`Cannot upload file head '${err.message}'`);
                    }

                    // Handle uploading chunks.
                    const uploadChunks = async (chunk, startIndex, endIndex, fullDigest) => {

                        const data = new Uint8Array(await chunk.arrayBuffer());
                        const compressedFile = gzip(data);

                        const digestHeader = `sha-256=${btoa(String.fromCharCode(...new Uint8Array(fullDigest)))}`;

                        try {
                            const result = await authenticatedFetchRetryWithAbort(user, `${coughMarshalRootUrl}/api/v1/uploads/${idRef.current}`, {
                                method: 'PATCH',
                                headers: {
                                    'Content-Type': 'application/octet-stream',
                                    'Content-Range': `bytes ${startIndex}-${endIndex}/${fileSize}`,
                                    'Content-Encoding': 'gzip',
                                    'Content-Disposition': `attachment; filename="24hrWAV${fileNumber}.wav"`,
                                    'Digest': digestHeader
                                },
                                body: compressedFile
                            }, retryCount, retryInterval, abortController.current)
                                .then(response => response.status === 200 || response.status === 202);

                            if (!result)
                                return false;
                        } catch (err) {
                            showAlert({ type: AlertCategoryEnum.Danger, message: err.message });
                            stopUpload();
                            throw new Error(`${err.message}`);
                        }
                    }

                    const fullHash = await computeFileHash(recordingFile);

                    // Calculate start and end indexes during the loop.
                    while (startIndex < fileSize) {
                        const endIndex = Math.min(startIndex + chunkSize, fileSize - 1);
                        const chunk = recordingFile.slice(startIndex, endIndex + 1);

                        await uploadChunks(chunk, startIndex, endIndex, fullHash);
                        startIndex = endIndex + 1;
                    }

                    return true;
                }

                // After finishing uploading process, complete and patch requests.
                try {
                    const success = await uploadFileToCoughMarshal(auth.user);
                    if (success) {
                        totalFileRef.current++;
                        const calculatedValue = (totalFileRef.current / (sortedFileGroups.length * 2)) * 100;
                        setUploadedFileNumber(Math.min(calculatedValue, 100));
                    }
                }
                catch (err) {
                    // Handle errors if the uploadFileTocoughMarshal has error.
                    showAlert({ type: AlertCategoryEnum.Danger, message: err.message });
                    stopUpload();
                    throw new Error(`${err.message}`);
                }

            }

            // Complete upload precess after finishing all chunks.
            const completeUpload = async (user) => {

                try {
                    const response = await authenticatedFetchRetryWithAbort(user, `${coughMarshalRootUrl}/api/v1/uploads/${idRef.current}/complete`, {
                        method: 'POST'
                    }, retryCount, retryInterval, abortController.current)

                    if (response.status === 200 || response.status === 202) {
                        const uploadfinalizedSuccess = await legacyClient.uploadFinalize(idRef.current);
                        return uploadfinalizedSuccess;

                    } else {
                        showAlert({ type: AlertCategoryEnum.Danger, message: AlertType.ERROR });
                        setIsComplete(false);
                        stopUpload();
                        return false;
                    }
                }

                catch (err) {
                    return false;
                }
            }

            try {
                const result = await completeUpload(auth.user);
                allUploadsSuccessful = result;
            } catch (err) {
                showAlert({ type: AlertCategoryEnum.Danger, message: err.message });
                stopUpload();
                throw new Error('Error patching upload');
            }
        }

        if (allUploadsSuccessful) {
            setAlert(null);
            setIsComplete(true);
            showAlert({ type: AlertCategoryEnum.Success, message: AlertType.SUCCESS })
        }
        else if (allUploadsSuccessful != null && !allUploadsSuccessful) {
            setIsComplete(false);
            throw new Error('Error completing upload');
        }

        fileCorrectness = null;
        timer.resume();
        totalFileRef.current = 0;
        setUploadedFileNumber(0);
        setIsUploading(false);
    }

    //Abort upload request, set accepted files and alert to null.
    const cancelUpload = () => {
        if (abortController.current) {
            abortController.current.abort();
        }
        fileCorrectness = false;
        setIsUploading(false);
        setSelectedFiles(undefined);
        totalFileRef.current = 0;
        setIsComplete(false);
        setAlert(null);
        setUploadedFileNumber(0);
        unAcceptedFile.current = [];
    }

    //Abort upload request.
    const stopUpload = () => {
        if (abortController.current) {
            abortController.current.abort();
        }
        fileCorrectness = false;
        setIsUploading(false);
        totalFileRef.current == 0;
        setIsComplete(false);
        setUploadedFileNumber(0);
    }

    return (
        <>
            <GlobalAuthorization currentPermissions={currentUserPermissions} permissionId={PermissionEnum.UploadRecordingFile}>
                <div className="row">
                    <div className="col-md-6">
                        <h2 className="text-primary">Upload Recording Files</h2>
                    </div>
                </div>

                {isUploading &&
                    <div className="row">
                        <div className="col-md-12">
                            <ProgressBar active now={uploadedFileNumber} label={`${uploadedFileNumber.toFixed(0)}%`} />
                        </div>
                    </div>}

                <div className="row">
                    <div className="col-md-12">
                        <div className="panel panel-default">
                            <div className="panel-body">
                                <div className="row text-center text-info">
                                    {isUploading && <p style={{ fontSize: 100 + 'px' }}>
                                        < FontAwesomeIcon icon={faCircleNotch} spin />
                                    </p>}

                                    {isUploading && !isComplete && <HelpBlock>Upload in progress...</HelpBlock>}

                                    <FormControl.Feedback />
                                </div>

                                <section>
                                    {!isUploading ? <div {...getRootProps()} style={dropzonestyle as React.CSSProperties} >
                                        <input {...getInputProps()} type="file" multiple />

                                        {isDragReject && (
                                            <HelpBlock>Only accept .wav file.</HelpBlock>)}
                                        {
                                            isDragActive ?
                                                <p>Drop the files here ...</p> :
                                                <p>Drag and drop some files here, or click to select files</p>
                                        }
                                    </div> : null}
                                    <aside>
                                        <HelpBlock>File name format: site__subject__visit__session__WAV1|2</HelpBlock>
                                        <HelpBlock>Example file name: 0001__0002__SCV1__V2C1__WAV1</HelpBlock>

                                        {(selectedFiles != undefined && acceptedFiles.length != 0) && (<>
                                            <label>Files</label> <ul>{files}</ul>
                                        </>)}

                                    </aside>
                                </section>

                                <Alert show={alert != null} category={alert?.type}>{alert?.message}

                                    {unAcceptedFile.current && unAcceptedFile.current.length > 0 && showUnAcceptedFile()}

                                </Alert>
                            </div>
                        </div>
                    </div>
                </div>

                <div className="row">
                    <div className="col-md-12 text-right">
                        <button className="btn btn-primary btn-outline" data-bs-dismiss="modal" onClick={() => cancelUpload()}>Cancel</button>
                        &nbsp;&nbsp;
                        <button className="btn btn-primary" disabled={!selectedFiles || isUploading || isComplete} onClick={() => { uploadRecordings(selectedFiles) }}>Upload</button>
                    </div>
                </div>
            </GlobalAuthorization>
        </>
    )

}
